import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScopeContextStorage } from '@/shared/lib/scope-storage';
import { secureSessionStorage } from '@/shared/lib/storage';
import { SESSION_EXPIRED_EVENT } from '@/shared/lib/session-events';
import { useToast } from '@/shared/hooks/use-toast';
import { useLanguage } from '@/shared/hooks/use-language';

const PUBLIC_PATHS = new Set([
  '/',
  '/login',
  '/forgot-password',
  '/reset-password',
]);

function isPublicPath(pathname: string): boolean {
  if (PUBLIC_PATHS.has(pathname)) return true;
  return pathname.startsWith('/user-invite/');
}

export function SessionExpiryWatcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    let handling = false;

    const handler = async () => {
      if (handling) return;
      handling = true;
      try {
        const sessionId = ScopeContextStorage.getCurrentSessionId();
        if (sessionId) {
          try {
            await secureSessionStorage.removeSessionKey(sessionId);
          } catch {
            // best-effort cleanup
          }
        }
        ScopeContextStorage.clearScopeContext();

        if (isPublicPath(location.pathname)) return;

        toast({
          title: t('session.expiredTitle'),
          description: t('session.expiredDescription'),
          variant: 'destructive',
        });
        navigate('/login', { replace: true });
      } finally {
        handling = false;
      }
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, [navigate, location.pathname, toast, t]);

  return null;
}
